import { on, createReducer, Action } from '@ngrx/store';
import { IAddress, ICartV2, ICoupon } from '@care-portals/api-interfaces';
import * as CheckoutActions from './checkout.actions';

export const FEATURE_KEY = 'checkoutV2';

export interface State {
  cart: ICartV2;
  error: Error;
  loading: boolean;
  orderId: number;
  addresses: {
    data: IAddress[];
    loading: boolean;
    selectedAddressIndex: number | 'new';
    newAddress: IAddress;
  };
  selectedPaymentMethodId: string; // this is the id of the SAVED payment method that is selected
  cvv: {
    showError: boolean;
    value: string;
  };
  coupon: ICoupon;
}

const initialState: State = {
  cart: null,
  error: null,
  loading: false,
  orderId: null,
  addresses: {
    data: [],
    selectedAddressIndex: null,
    loading: false,
    newAddress: null
  },
  coupon: null,
  selectedPaymentMethodId: 'new',
  cvv: {
    showError: false,
    value: ''
  }
};

const _reducer = createReducer(
  { ...initialState },
  on(CheckoutActions.loadCart, (state: State, {}) => {
    return {
      ...state,
      error: null,
      loading: true
    };
  }),

  on(CheckoutActions.loadCartSuccess, (state: State, { cart }) => {
    return {
      ...state,
      cart,
      error: null,
      loading: false
    };
  }),

  on(CheckoutActions.loadCartFailure, (state: State, { error }) => {
    return {
      ...state,
      error,
      loading: false
    };
  }),

  on(CheckoutActions.startCheckout, (state: State, {}) => {
    return {
      ...state,
      error: null,
      loading: true
    };
  }),

  on(
    CheckoutActions.startCheckoutSuccess,
    (state: State, { cart, paymentMethods }) => {
      return {
        ...state,
        cart,
        error: null,
        loading: false
      };
    }
  ),

  on(CheckoutActions.startCheckoutFailure, (state: State, { error }) => {
    return {
      ...state,
      error,
      loading: false
    };
  }),

  on(CheckoutActions.applyCoupon, (state: State) => {
    return {
      ...state,
      loading: true
    };
  }),

  on(CheckoutActions.applyCouponSuccess, (state: State, { cart, coupon }) => {
    return {
      ...state,
      cart,
      loading: false,
      coupon
    };
  }),

  on(CheckoutActions.applyCouponFailure, (state: State, {}) => {
    return {
      ...state,
      coupon: null,
      loading: false
    };
  }),

  on(
    CheckoutActions.setSelectedAddressIndex,
    (state: State, { selectedAddressIndex }) => {
      return {
        ...state,
        addresses: {
          ...state.addresses,
          selectedAddressIndex
        },
        error: null
      };
    }
  ),

  on(CheckoutActions.setNewAddress, (state: State, { address }) => {
    return {
      ...state,
      addresses: {
        ...state.addresses,
        newAddress: address,
        loading: false,
        selectedAddressIndex: 'new' as 'new'
      },
      error: null
    };
  }),

  on(CheckoutActions.loadAddresses, (state: State) => {
    return {
      ...state,
      addresses: { ...state.addresses, loading: true },
      error: null,
      loading: true
    };
  }),

  on(CheckoutActions.loadAddressesSuccess, (state: State, { addresses }) => {
    return {
      ...state,
      addresses: {
        data: addresses,
        loading: false,
        newAddress: null,
        selectedAddressIndex:
          addresses.length > 0 ? 0 : ('new' as number | 'new')
      },
      loading: false
    };
  }),

  on(
    CheckoutActions.setSelectedPaymentMethod,
    (state: State, { paymentMethodId }) => {
      return {
        ...state,
        selectedPaymentMethodId: paymentMethodId
      };
    }
  ),

  on(CheckoutActions.loadAddressesFailure, (state: State, { error }) => {
    return {
      ...state,
      addresses: { ...state.addresses, loading: false },
      error: error,
      loading: false
    };
  }),

  on(CheckoutActions.finalizeCheckout, (state: State, {}) => {
    return {
      ...state,
      error: null,
      loading: true
    };
  }),

  on(CheckoutActions.finalizeCheckoutSuccess, (state: State, { order }) => {
    return {
      ...state,
      error: null,
      orderId: order.id,
      loading: false
    };
  }),

  on(CheckoutActions.finalizeCheckoutFailure, (state: State, { error }) => {
    return {
      ...state,
      error: {
        message: error?.message || 'Unknown error',
        name: error?.name || 'Unknown error'
      },
      loading: false
    };
  }),

  on(CheckoutActions.setCvv, (state: State, { value, showError }) => {
    return {
      ...state,
      cvv: { value, showError }
    };
  })
);

export function reducer(state: State | undefined, action: Action) {
  return _reducer(state, action);
}
